import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { api } from "@/services/api/client";
import type { Subscription, SubscriptionTier } from "@/types/billing";

/**
 * Billing Store
 * Manages subscription state, checkout sessions, and billing portal access.
 * Follows the same subscribeWithSelector + immer pattern as organization-store.
 */

interface BillingState {
  // State
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchSubscription: () => Promise<void>;
  createCheckoutSession: (
    tier: SubscriptionTier,
    interval: 'monthly' | 'annual',
    kind?: 'subscription' | 'graduation',
  ) => Promise<void>;
  createPortalSession: () => Promise<void>;
  cancelSubscription: () => Promise<void>;
  startTrial: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const STRIPE_BILLING_REDIRECT_HOSTS = new Set([
  "checkout.stripe.com",
  "billing.stripe.com",
]);

function redirectToBillingUrl(rawUrl: string): void {
  const url = new URL(rawUrl, window.location.origin);
  const isSameOrigin = url.origin === window.location.origin;
  const isStripeBilling =
    url.protocol === "https:" && STRIPE_BILLING_REDIRECT_HOSTS.has(url.hostname);

  if (!isSameOrigin && !isStripeBilling) {
    throw new Error("Unexpected billing redirect URL");
  }

  window.location.assign(url.toString());
}

export const useBillingStore = create<BillingState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      subscription: null,
      isLoading: false,
      error: null,

      // Fetch current subscription for the active organization
      fetchSubscription: async () => {
        if (get().isLoading) return;

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.get<Subscription>("/billing/subscription");

          set((state) => {
            state.subscription = response.data || null;
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to fetch subscription";
            state.isLoading = false;
          });
        }
      },

      // Create a Stripe Checkout session and redirect
      createCheckoutSession: async (
        tier: SubscriptionTier,
        interval: 'monthly' | 'annual',
        kind: 'subscription' | 'graduation' = 'subscription',
      ) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.post<
            { url: string },
            {
              tier: SubscriptionTier;
              billingInterval: 'monthly' | 'annual';
              successUrl: string;
              cancelUrl: string;
              kind: 'subscription' | 'graduation';
            }
          >("/billing/checkout", {
            tier,
            billingInterval: interval,
            successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/pricing`,
            kind,
          });

          set((state) => {
            state.isLoading = false;
          });

          if (response.data?.url) {
            redirectToBillingUrl(response.data.url);
          }
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to create checkout session";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Create a Stripe Customer Portal session and redirect
      createPortalSession: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.post<
            { url: string },
            { returnUrl: string }
          >("/billing/portal", {
            returnUrl: `${window.location.origin}/billing`,
          });

          set((state) => {
            state.isLoading = false;
          });

          if (response.data?.url) {
            redirectToBillingUrl(response.data.url);
          }
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to create portal session";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Cancel subscription at period end
      cancelSubscription: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          await api.delete("/billing/subscription");

          set((state) => {
            if (state.subscription) {
              state.subscription.cancelAtPeriodEnd = true;
            }
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to cancel subscription";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Start a free trial
      startTrial: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.post<Subscription>("/billing/trial", {});

          set((state) => {
            state.subscription = response.data || null;
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to start trial";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Clear error state
      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },

      // Reset store (on logout or org switch)
      reset: () => {
        set((state) => {
          state.subscription = null;
          state.isLoading = false;
          state.error = null;
        });
      },
    })),
  ),
);

// Subscribe to auth store to reset billing store on logout
if (typeof window !== "undefined") {
  import("../auth/auth-store").then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, previousIsAuthenticated) => {
        if (previousIsAuthenticated && !isAuthenticated) {
          useBillingStore.getState().reset();
        }
      },
    );
  });
}
